import { useEffect, useState } from 'react';
import { BadgeLabel, CheckIcon, isWeb, XIcon } from 'smoothie';

const BROWSER_MEDIA_PROBE_TIMEOUT_MS = 20_000;

/**
 * Cross-origin URLs often return 200 for GET but block `fetch`/`HEAD` in the
 * browser without CORS. Image and video elements use the same load path as
 * opening the URL in a new tab, so they reflect reachability for these assets.
 */
const probeRemotePosterImage = async (url: string): Promise<boolean> => {
    return await new Promise((resolve) => {
        if (!isWeb) {
            // This check is only supported on web
            resolve(false);
            return;
        }

        const img = new window.Image();
        let settled = false;

        const finish = (ok: boolean) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            img.onload = null;
            img.onerror = null;
            resolve(ok);
        };

        const timer = window.setTimeout(() => {
            finish(false);
        }, BROWSER_MEDIA_PROBE_TIMEOUT_MS);

        img.onload = () => {
            finish(true);
        };
        img.onerror = () => {
            finish(false);
        };
        img.src = url;
    });
};

/**
 * Cross-origin URLs often return 200 for GET but block `fetch`/`HEAD` in the
 * browser without CORS. Image and video elements use the same load path as
 * opening the URL in a new tab, so they reflect reachability for these assets.
 */
const probeRemoteVideoMetadata = async (url: string): Promise<boolean> => {
    return await new Promise((resolve) => {
        if (!isWeb) {
            // This check is only supported on web
            resolve(false);
            return;
        }

        const video = document.createElement('video');
        video.preload = 'metadata';
        let settled = false;

        const finish = (ok: boolean) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            video.removeEventListener('loadedmetadata', onLoadedMetadata);
            video.removeEventListener('error', onError);
            video.removeAttribute('src');
            video.load();
            resolve(ok);
        };

        const timer = window.setTimeout(() => {
            finish(false);
        }, BROWSER_MEDIA_PROBE_TIMEOUT_MS);

        const onLoadedMetadata = () => {
            finish(true);
        };
        const onError = () => {
            finish(false);
        };

        video.addEventListener('loadedmetadata', onLoadedMetadata);
        video.addEventListener('error', onError);
        video.src = url;
    });
};

const useDetermineMediaStatus = (
    url: string | null | undefined,
    mediaType: 'video' | 'image',
) => {
    const [status, setStatus] = useState<boolean | null>(null);

    useEffect(() => {
        setStatus(null);
        if (url == null || !isWeb) {
            return;
        }

        let cancelled = false;

        const checkMedia = async () => {
            const status = await (mediaType === 'video'
                ? probeRemoteVideoMetadata(url)
                : probeRemotePosterImage(url));

            if (!cancelled) {
                setStatus(status);
            }
        };

        checkMedia().catch(() => {
            if (!cancelled) {
                setStatus(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [url, mediaType]);

    return status;
};

export const GuideMediaStatusBadge = ({
    label,
    url,
    mediaType,
}: {
    label: string;
    url: string | null | undefined;
    mediaType: 'video' | 'image';
}) => {
    const reachable = useDetermineMediaStatus(url, mediaType);

    if (!isWeb) {
        return null;
    }

    const badge =
        reachable == null ? (
            <BadgeLabel variant="gray" size="regular" text="checking…" />
        ) : (
            <BadgeLabel
                icon={reachable ? CheckIcon : XIcon}
                variant={reachable ? 'positive' : 'danger'}
                size="regular"
                text={label}
            />
        );

    if (url == null || url === '' || reachable == null) {
        return badge;
    }

    return (
        <a href={url} target="_blank" rel="noreferrer" title={url}>
            {badge}
        </a>
    );
};
